import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../../../design-system/button/button';
import { FormTextField } from '../../../design-system/form-text-field/form-text-field';
import { Form } from '../../../design-system/form/form-primitives';
import { Stack } from '../../../design-system/stack/stack';

const inviteMemberSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(50, 'Must be less than 50 characters'),
  lastName: z.string().min(1, 'Last name is required').max(50, 'Must be less than 50 characters'),
  email: z.string().email('Please enter a valid email address'),
  role: z.string().min(1, 'Please select a role'),
});

export type InviteMemberFormData = z.infer<typeof inviteMemberSchema>;

const ROLE_OPTIONS = [
  {
    value: 'admin',
    label: 'Admin',
    description:
      'An Admin has full access to all accounts, including the ability to make payments and add new admin members.',
  },
];

interface InviteMemberFormProps {
  formValues: InviteMemberFormData;
  isLoading?: boolean;
  onCancel: () => void;
  onSubmit: (formValues: InviteMemberFormData) => void;
}

export const InviteMemberForm = ({ formValues, isLoading, onCancel, onSubmit }: InviteMemberFormProps) => {
  const form = useForm<InviteMemberFormData>({
    resolver: zodResolver(inviteMemberSchema),
    defaultValues: {
      firstName: formValues?.firstName,
      lastName: formValues?.lastName,
      email: formValues?.email,
      role: 'admin',
    },
    mode: 'onTouched',
    disabled: isLoading,
  });

  const selectedRole = form.watch('role');
  const selectedRoleConfig = ROLE_OPTIONS.find((role) => role.value === selectedRole);

  const handleCancel = () => {
    form.reset();
    onCancel();
  };

  return (
    <div className="max-w-lg bg-white">
      <Form {...form}>
        <form onSubmit={form.handleSubmit((data) => onSubmit(data))} className="space-y-4" noValidate>
          <div className="flex w-full justify-between gap-2">
            <div className="flex-1">
              <FormTextField
                inputName="firstName"
                label="First Name"
                placeholder="Enter first name"
                isDisabled={isLoading}
                disablePasswordManager
              />
            </div>
            <div className="flex-1">
              <FormTextField
                inputName="lastName"
                label="Last Name"
                placeholder="Enter last name"
                isDisabled={isLoading}
                disablePasswordManager
              />
            </div>
          </div>

          <FormTextField
            inputName="email"
            label="Email Address"
            placeholder="Enter email address"
            isDisabled={isLoading}
            disablePasswordManager
          />

          {/* TODO: Add a select field for the role when we have more than one role*/}
          <div className="flex flex-col gap-2 rounded-lg border border-border bg-white px-4 py-3">
            {ROLE_OPTIONS.map((role) => (
              <Stack key={role.value} space={1}>
                <div className="text-xs font-medium text-muted-foreground">Role</div>
                <div className="text-black">{role.label}</div>
              </Stack>
            ))}
          </div>

          {selectedRoleConfig && (
            <div className="border-0.5 rounded-lg border-border bg-muted px-4 py-3">
              <div className="text-sm text-black">{selectedRoleConfig.description}</div>
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button type="submit" className="w-full" disabled={!form.formState.isValid || isLoading}>
              Send Invite
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancel}
              disabled={isLoading}
              className="w-full hover:bg-border"
            >
              Cancel
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};
